import React, { useState } from 'react';
import { 
  Box, 
  Typography, 
  Paper, 
  List, 
  ListItem, 
  ListItemIcon, 
  ListItemText,
  Divider,
  Chip,
  Grid
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { 
  Campaign as CampaignIcon, 
  Group as AudienceIcon, 
  BarChart as AnalyticsIcon, 
  Palette as BrandingIcon,
  MonetizationOn as BudgetIcon,
  Schedule as TimelineIcon,
  CheckCircle as GoalIcon,
  CheckCircle
} from '@mui/icons-material';
import HolographicStepper, { StepData } from '../common/HolographicStepper.tsx';
import GradientText from '../common/GradientText.tsx';
import EditableStepContent from '../common/EditableStepContent.tsx';

interface MarketingPlanStepperProps {
  productName?: string;
  initialStep?: number;
  onComplete?: () => void;
  onSubmitRFP?: () => void;
}

const ContentBox = styled(Box)(({ theme }) => ({
  padding: theme.spacing(2),
  background: 'rgba(26, 26, 46, 0.4)',
  borderRadius: theme.spacing(1),
  border: '1px solid rgba(138, 43, 226, 0.2)',
  marginBottom: theme.spacing(2),
}));

const MetricChip = styled(Chip)(({ theme }) => ({
  margin: theme.spacing(0.5),
  backgroundColor: 'rgba(0, 204, 153, 0.1)',
  color: '#00CC99',
  border: '1px solid rgba(0, 204, 153, 0.3)',
  '&:hover': {
    backgroundColor: 'rgba(0, 204, 153, 0.2)',
  }
}));

const MarketingPlanStepper: React.FC<MarketingPlanStepperProps> = ({ 
  productName = "Product", 
  initialStep = 0,
  onComplete,
  onSubmitRFP
}) => {
  // Add state for costs
  const [stepCosts, setStepCosts] = useState({
    0: 3200, // Market Research
    1: 2800, // Campaign Strategy
    2: 2500, // Branding & Messaging
    3: 4200, // Budget Allocation
    4: 2300, // Timeline & Execution
    5: 1800, // Measurement & Analytics
  });

  // Function to update a step's cost
  const handleCostChange = (stepIndex: number, newCost: number) => {
    setStepCosts(prev => ({
      ...prev,
      [stepIndex]: newCost
    }));
  };

  // Define the marketing plan steps
  const marketingSteps: StepData[] = [
    {
      label: 'Market Research',
      description: 'Analyze market trends and identify target audience',
      icon: <AudienceIcon />,
      cost: stepCosts[0],
      content: (
        <Box>
          <GradientText gradient="primary" variant="h6" sx={{ mb: 2 }}>
            Market Research for {productName}
          </GradientText>

          <EditableStepContent
            title="Target Audience Analysis"
            cost={stepCosts[0]}
            onCostChange={(newCost) => handleCostChange(0, newCost)}
            content={
              <List dense>
                <ListItem>
                  <ListItemIcon sx={{ minWidth: 36, color: '#00CC99' }}>
                    <CheckCircle fontSize="small" />
                  </ListItemIcon>
                  <ListItemText 
                    primary="Demographic Profiling" 
                    secondary="Age, gender, location, income level" 
                    secondaryTypographyProps={{ color: 'rgba(255, 255, 255, 0.7)' }}
                  />
                </ListItem>
                <ListItem>
                  <ListItemIcon sx={{ minWidth: 36, color: '#00CC99' }}>
                    <CheckCircle fontSize="small" />
                  </ListItemIcon>
                  <ListItemText 
                    primary="Psychographic Analysis" 
                    secondary="Interests, values, lifestyle, personality traits" 
                    secondaryTypographyProps={{ color: 'rgba(255, 255, 255, 0.7)' }}
                  />
                </ListItem>
                <ListItem>
                  <ListItemIcon sx={{ minWidth: 36, color: '#00CC99' }}>
                    <CheckCircle fontSize="small" />
                  </ListItemIcon>
                  <ListItemText 
                    primary="Behavioral Patterns" 
                    secondary="Purchase habits, brand interactions, device usage" 
                    secondaryTypographyProps={{ color: 'rgba(255, 255, 255, 0.7)' }}
                  />
                </ListItem>
              </List>
            }
          />
          
          <ContentBox>
            <Typography variant="subtitle1" sx={{ color: '#fff', mb: 1 }}>
              Competitor Analysis
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <Paper sx={{ p: 2, bgcolor: 'rgba(138, 43, 226, 0.1)', height: '100%' }}>
                  <Typography variant="subtitle2" sx={{ color: '#8A2BE2', mb: 1 }}>
                    Direct Competitors
                  </Typography>
                  <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                    Analyze pricing strategies, market share, and unique selling propositions of direct competitors.
                  </Typography>
                </Paper>
              </Grid>
              <Grid item xs={12} md={6}>
                <Paper sx={{ p: 2, bgcolor: 'rgba(0, 204, 153, 0.1)', height: '100%' }}>
                  <Typography variant="subtitle2" sx={{ color: '#00CC99', mb: 1 }}>
                    Indirect Competitors
                  </Typography>
                  <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                    Identify alternative solutions and potential market disruptors.
                  </Typography>
                </Paper>
              </Grid>
            </Grid>
          </ContentBox>
        </Box>
      )
    },
    {
      label: 'Campaign Strategy',
      description: 'Define marketing objectives and campaign approach',
      icon: <CampaignIcon />,
      cost: stepCosts[1],
      content: (
        <Box>
          <GradientText gradient="primary" variant="h6" sx={{ mb: 2 }}>
            Campaign Strategy for {productName}
          </GradientText>
          
          <EditableStepContent
            title="Marketing Objectives"
            cost={stepCosts[1]}
            onCostChange={(newCost) => handleCostChange(1, newCost)}
            content={
              <List dense>
                <ListItem>
                  <ListItemIcon sx={{ minWidth: 36, color: '#00CC99' }}>
                    <GoalIcon fontSize="small" />
                  </ListItemIcon>
                  <ListItemText 
                    primary="Increase brand awareness by 35% in Q3" 
                    secondaryTypographyProps={{ color: 'rgba(255, 255, 255, 0.7)' }}
                  />
                </ListItem>
                <ListItem>
                  <ListItemIcon sx={{ minWidth: 36, color: '#00CC99' }}>
                    <GoalIcon fontSize="small" />
                  </ListItemIcon>
                  <ListItemText 
                    primary="Generate 2,500 qualified leads per month" 
                    secondaryTypographyProps={{ color: 'rgba(255, 255, 255, 0.7)' }}
                  />
                </ListItem>
                <ListItem>
                  <ListItemIcon sx={{ minWidth: 36, color: '#00CC99' }}>
                    <GoalIcon fontSize="small" />
                  </ListItemIcon>
                  <ListItemText 
                    primary="Achieve 15% conversion rate from trial to paid" 
                    secondaryTypographyProps={{ color: 'rgba(255, 255, 255, 0.7)' }}
                  />
                </ListItem>
              </List>
            }
          />
          
          <ContentBox>
            <Typography variant="subtitle1" sx={{ color: '#fff', mb: 1 }}>
              Campaign Approach
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} md={4}>
                <Paper sx={{ p: 2, bgcolor: 'rgba(138, 43, 226, 0.1)', height: '100%' }}>
                  <Typography variant="subtitle2" sx={{ color: '#8A2BE2', mb: 1 }}>
                    Awareness Phase
                  </Typography>
                  <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                    Content marketing, social media campaigns, and PR initiatives to build brand recognition.
                  </Typography>
                </Paper>
              </Grid>
              <Grid item xs={12} md={4}>
                <Paper sx={{ p: 2, bgcolor: 'rgba(0, 204, 153, 0.1)', height: '100%' }}>
                  <Typography variant="subtitle2" sx={{ color: '#00CC99', mb: 1 }}>
                    Consideration Phase
                  </Typography>
                  <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                    Webinars, case studies, and product demos to showcase value proposition.
                  </Typography>
                </Paper>
              </Grid>
              <Grid item xs={12} md={4}>
                <Paper sx={{ p: 2, bgcolor: 'rgba(255, 111, 0, 0.1)', height: '100%' }}>
                  <Typography variant="subtitle2" sx={{ color: '#FF6F00', mb: 1 }}>
                    Decision Phase
                  </Typography>
                  <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                    Free trials, consultations, and targeted offers to convert prospects.
                  </Typography>
                </Paper>
              </Grid>
            </Grid>
          </ContentBox>
        </Box>
      )
    },
    {
      label: 'Branding & Messaging',
      description: 'Develop brand identity and key messaging',
      icon: <BrandingIcon />,
      cost: stepCosts[2],
      content: (
        <Box>
          <GradientText gradient="primary" variant="h6" sx={{ mb: 2 }}>
            Branding & Messaging for {productName}
          </GradientText>
          
          <EditableStepContent
            title="Brand Positioning"
            cost={stepCosts[2]}
            onCostChange={(newCost) => handleCostChange(2, newCost)}
            content={
              <Paper sx={{ p: 2, bgcolor: 'rgba(26, 26, 46, 0.6)', mb: 2 }}>
                <Typography variant="body1" sx={{ color: '#fff', fontStyle: 'italic' }}>
                  &ldquo;{productName} is the premier AI-powered solution for executives who demand intelligent insights and seamless automation, enabling unprecedented decision-making capabilities in a rapidly evolving business landscape.&rdquo;
                </Typography>
              </Paper>
            }
          />
            
          <Divider sx={{ my: 2, bgcolor: 'rgba(138, 43, 226, 0.2)' }} />
            
          <Typography variant="subtitle1" sx={{ color: '#fff', mb: 1 }}>
            Key Messaging Pillars
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} md={4}>
              <Paper sx={{ p: 2, bgcolor: 'rgba(138, 43, 226, 0.1)', height: '100%' }}>
                <Typography variant="subtitle2" sx={{ color: '#8A2BE2', mb: 1 }}>
                  Innovation
                </Typography>
                <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                  Cutting-edge AI technology that evolves with your business needs
                </Typography>
              </Paper>
            </Grid>
            <Grid item xs={12} md={4}>
              <Paper sx={{ p: 2, bgcolor: 'rgba(0, 204, 153, 0.1)', height: '100%' }}>
                <Typography variant="subtitle2" sx={{ color: '#00CC99', mb: 1 }}>
                  Efficiency
                </Typography>
                <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                  Streamlined workflows that save time and reduce operational costs
                </Typography>
              </Paper>
            </Grid>
            <Grid item xs={12} md={4}>
              <Paper sx={{ p: 2, bgcolor: 'rgba(255, 111, 0, 0.1)', height: '100%' }}>
                <Typography variant="subtitle2" sx={{ color: '#FF6F00', mb: 1 }}>
                  Reliability
                </Typography>
                <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                  Enterprise-grade security and performance you can depend on
                </Typography>
              </Paper>
            </Grid>
          </Grid>
        </Box>
      )
    },
    {
      label: 'Budget Allocation',
      description: 'Allocate resources across marketing channels',
      icon: <BudgetIcon />,
      cost: stepCosts[3],
      content: (
        <Box>
          <GradientText gradient="primary" variant="h6" sx={{ mb: 2 }}>
            Budget Allocation for {productName}
          </GradientText>
          
          <EditableStepContent
            title="Marketing Budget Breakdown"
            cost={stepCosts[3]}
            onCostChange={(newCost) => handleCostChange(3, newCost)}
            content={
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <Paper sx={{ p: 2, bgcolor: 'rgba(26, 26, 46, 0.6)' }}>
                    <Typography variant="subtitle2" sx={{ color: '#fff', mb: 2 }}>
                      Channel Distribution
                    </Typography>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <Box sx={{ width: '40%' }}>
                          <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                            Digital Advertising
                          </Typography>
                        </Box>
                        <Box sx={{ width: '50%', bgcolor: 'rgba(0, 0, 0, 0.2)', borderRadius: 1, mr: 1 }}>
                          <Box sx={{ width: '35%', height: 8, bgcolor: '#8A2BE2', borderRadius: 1 }} />
                        </Box>
                        <Typography variant="body2" sx={{ color: '#8A2BE2', width: '10%' }}>
                          35%
                        </Typography>
                      </Box>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <Box sx={{ width: '40%' }}>
                          <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                            Content Marketing
                          </Typography>
                        </Box>
                        <Box sx={{ width: '50%', bgcolor: 'rgba(0, 0, 0, 0.2)', borderRadius: 1, mr: 1 }}>
                          <Box sx={{ width: '25%', height: 8, bgcolor: '#00CC99', borderRadius: 1 }} />
                        </Box>
                        <Typography variant="body2" sx={{ color: '#00CC99', width: '10%' }}>
                          25%
                        </Typography>
                      </Box>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <Box sx={{ width: '40%' }}>
                          <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                            Events & Webinars
                          </Typography>
                        </Box>
                        <Box sx={{ width: '50%', bgcolor: 'rgba(0, 0, 0, 0.2)', borderRadius: 1, mr: 1 }}>
                          <Box sx={{ width: '20%', height: 8, bgcolor: '#FF6F00', borderRadius: 1 }} />
                        </Box>
                        <Typography variant="body2" sx={{ color: '#FF6F00', width: '10%' }}>
                          20%
                        </Typography>
                      </Box>
                    </Box>
                  </Paper>
                </Grid>
              </Grid>
            }
          />
        </Box>
      )
    },
    {
      label: 'Timeline & Execution',
      description: 'Create implementation schedule and assign responsibilities',
      icon: <TimelineIcon />,
      cost: stepCosts[4],
      content: (
        <Box>
          <GradientText gradient="primary" variant="h6" sx={{ mb: 2 }}>
            Timeline & Execution for {productName}
          </GradientText>
          
          <EditableStepContent
            title="Implementation Schedule"
            cost={stepCosts[4]}
            onCostChange={(newCost) => handleCostChange(4, newCost)}
            content={
              <Paper sx={{ p: 2, bgcolor: 'rgba(26, 26, 46, 0.6)', mb: 2 }}>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  <Box>
                    <Typography variant="subtitle2" sx={{ color: '#8A2BE2', mb: 0.5 }}>
                      Phase 1: Preparation (Weeks 1-2)
                    </Typography>
                    <List dense>
                      <ListItem>
                        <ListItemText 
                          primary="Finalize campaign assets and messaging" 
                          secondary="Owner: Creative Team" 
                          secondaryTypographyProps={{ color: 'rgba(255, 255, 255, 0.5)' }}
                        />
                      </ListItem>
                      <ListItem>
                        <ListItemText 
                          primary="Set up tracking and analytics" 
                          secondary="Owner: Data Team" 
                          secondaryTypographyProps={{ color: 'rgba(255, 255, 255, 0.5)' }}
                        />
                      </ListItem>
                    </List>
                  </Box>
                  
                  <Box>
                    <Typography variant="subtitle2" sx={{ color: '#00CC99', mb: 0.5 }}>
                      Phase 2: Launch (Weeks 3-4)
                    </Typography>
                    <List dense>
                      <ListItem>
                        <ListItemText 
                          primary="Roll out digital advertising campaigns" 
                          secondary="Owner: Paid Media Team" 
                          secondaryTypographyProps={{ color: 'rgba(255, 255, 255, 0.5)' }}
                        />
                      </ListItem>
                    </List>
                  </Box>
                </Box>
              </Paper>
            }
          />
        </Box>
      )
    },
    {
      label: 'Measurement & Analytics',
      description: 'Define KPIs and tracking methodology',
      icon: <AnalyticsIcon />,
      cost: stepCosts[5],
      content: (
        <Box>
          <GradientText gradient="primary" variant="h6" sx={{ mb: 2 }}>
            Measurement & Analytics for {productName}
          </GradientText>
          
          <EditableStepContent
            title="Key Performance Indicators"
            cost={stepCosts[5]}
            onCostChange={(newCost) => handleCostChange(5, newCost)}
            content={
              <Box sx={{ display: 'flex', flexWrap: 'wrap', mb: 2 }}>
                <MetricChip label="Impressions" />
                <MetricChip label="Click-through Rate" />
                <MetricChip label="Cost per Acquisition" />
                <MetricChip label="Conversion Rate" />
                <MetricChip label="Customer Lifetime Value" />
                <MetricChip label="Return on Ad Spend" />
                <MetricChip label="Brand Awareness" />
                <MetricChip label="Net Promoter Score" />
              </Box>
            }
          />
            
          <Divider sx={{ my: 2, bgcolor: 'rgba(138, 43, 226, 0.2)' }} />
            
          <Typography variant="subtitle1" sx={{ color: '#fff', mb: 1 }}>
            Reporting Framework
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} md={4}>
              <Paper sx={{ p: 2, bgcolor: 'rgba(138, 43, 226, 0.1)', height: '100%' }}>
                <Typography variant="subtitle2" sx={{ color: '#8A2BE2', mb: 1 }}>
                  Weekly Monitoring
                </Typography>
                <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                  Real-time dashboards tracking campaign performance metrics with daily alerts for significant changes.
                </Typography>
              </Paper>
            </Grid>
            <Grid item xs={12} md={4}>
              <Paper sx={{ p: 2, bgcolor: 'rgba(0, 204, 153, 0.1)', height: '100%' }}>
                <Typography variant="subtitle2" sx={{ color: '#00CC99', mb: 1 }}>
                  Monthly Reviews
                </Typography>
                <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                  Comprehensive analysis of all KPIs with actionable insights and optimization recommendations.
                </Typography>
              </Paper>
            </Grid>
          </Grid>
        </Box>
      )
    }
  ];

  return (
    <Box>
      <HolographicStepper 
        steps={marketingSteps}
        initialStep={initialStep}
        onComplete={onComplete}
        orientation="horizontal"
        alternativeLabel={true}
      />
    </Box>
  );
};

export default MarketingPlanStepper; 